import type { Resource } from '../../types';
import { logger } from '../../utils/logger';
import { mapApiTypeToDisplayName } from '../../utils/resourceTypeMapper';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

export const fetchResourcesFromService = async (
  bearerToken: string,
  orgName: string,
  resourceType: string
): Promise<Resource[]> => {
  const baseUrl = getEnv().VITE_EDCI_API_URL;
  const url = `${baseUrl}/api/genesysInfo/${orgName}/${resourceType}`;

  try {
    const response = await authenticatedFetch(url, {
      headers: getV2AuthHeaders(bearerToken)
    });

    if (!response.ok) {
      logger.error('Fetch resources API returned error', {
        status: response.status,
        statusText: response.statusText,
        organization: orgName,
        resourceType,
        url
      });
      throw new Error(`Error fetching resources: ${response.status} ${response.statusText}`);
    }

    // API returns array directly, not wrapped in entities
    const data: Resource[] = await response.json();
    logger.debug('Resources fetched successfully', {
      organization: orgName,
      resourceType,
      count: data.length
    });

    // Map the API response to your Resource interface
    return data.map((resource: Resource) => {
      const mapped: Resource = {
        id: resource.id || '',
        name: resource.name || '',
        type: mapApiTypeToDisplayName(resource.type) || resourceType
      };
      if (resource.division !== undefined) {
        mapped.division = resource.division;
      }
      return mapped;
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to fetch resources', {
      error: msg,
      organization: orgName,
      resourceType,
      url
    });
    throw error;
  }
};

export default fetchResourcesFromService;
